<template>
  <div>
      <!-- Search Form Section -->
    <div class="relative bg-cover bg-center min-h-[600px]" :style="{ backgroundImage: 'url(/img/search-hero-bg.webp)' }">
      
      <div class="lg:container flex flex-row mx-auto xl:px-32 sm:px-4 px-2 lg:py-16 py-10 space-x-8 relative z-20">
        <div ref="searchFormContainer"
          class="transition-all duration-300 w-full sm:w-[400px]">

          <!-- Mobile title text (only visible on mobile) -->
          <h1 class="md:hidden lg:text-5xl text-2xl font-semibold text-white text-opacity-100 [text-shadow:_0_2px_4px_rgb(0_0_0_/_90%)] mb-3 text-center">
            {{ currentTitle }}
          </h1>

          <!-- Unified card: tabs + form -->
          <div class="bg-white/20 backdrop-blur-sm rounded-2xl relative z-40">

            <!-- Search Type Switcher (inside card, top) -->
            <div class="flex gap-1 p-1.5">
              <button v-for="type in searchTypes" :key="type.id"
                @click="setSearchType(type.id)"
                :class="[
                  'relative flex-1 flex items-center justify-center py-2.5 px-2 rounded-xl text-xs font-semibold transition-all duration-200',
                  activeSearchType === type.id
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-white/80 hover:text-white hover:bg-white/20 tab-inactive-pulse'
                ]"
              >
                {{ activeSearchType === type.id ? type.label : type.shortLabel }}
                <span
                  v-if="type.isNew"
                  class="pointer-events-none absolute -top-0.5 -right-0.5 px-1 py-px text-[8px] font-bold rounded bg-amber-700 text-white leading-none shadow-sm"
                >{{ t('home.new') }}</span>
              </button>
            </div>

            <!-- Divider -->
            <div class="h-px bg-white/20 mx-3"></div>

            <!-- Basic / Weather SearchForm -->
            <SearchForm
              ref="searchFormRef"
              v-if="activeSearchType !== 'buddy'"
              @search-loading="handleLoading"
              @search-error="handleError"
              @search-results="handleSearchResults"
              @search-params-update="handleSearchParamsUpdate"
              @navigate="handleNavigation"
              :passedSearchParams="searchParams"
              :searchMode="activeSearchType"
            />

            <!-- Buddy Search Form -->
            <BuddySearchForm
              v-else
              :key="buddyFormKey"
              :initial-destinations="searchParams.to || []"
              @update:destinations="updateBuddyDestinations"
            />

          </div>

          <!-- Mobile subtitle text (only visible on mobile) -->
          <p class="md:hidden text-white/80 text-xs mt-3 text-center [text-shadow:_0_1px_3px_rgb(0_0_0_/_60%)]">
            {{ currentSubtitle }}
          </p>

        </div>

        <div class="flex-1 flex flex-col items-start justify-start pt-14 mx-auto md:block hidden">
          <div class="w-11/12 lg:w-10/12 mx-auto space-y-10">
            <div class="flex flex-col space-y-5">
              <h1 class="lg:text-5xl text-3xl font-semibold text-white text-opacity-100 [text-shadow:_0_2px_4px_rgb(0_0_0_/_90%)]">
                {{ currentTitle }}
              </h1>
              <p class="text-white/90 font-bold text-lg [text-shadow:_0_2px_4px_rgb(0_0_0_/_80%)]">
                  {{ currentSubtitle }}
              </p>
            </div>
            <div class="border-t border-white/20 pt-5">
              <p class="text-white/70 text-sm mb-2 [text-shadow:_0_1px_3px_rgb(0_0_0_/_60%)]">{{ t('home.notSureWhereToGo') }}</p>
              <router-link
                to="/explore"
                class="inline-flex items-center gap-2 px-4 py-2 bg-white/15 backdrop-blur-sm border border-white/25 rounded-lg text-white text-sm font-semibold hover:bg-white/25 transition-all duration-200 [text-shadow:_0_1px_2px_rgb(0_0_0_/_50%)]"
              >
                <GlobeEuropeAfricaIcon class="h-4 w-4" />
                {{ t('home.exploreDestinations') }}
              </router-link>
            </div>
          </div>
        </div>

      </div>

      
    </div>

    <div class="lg:container mx-auto xl:px-32 sm:px-4 px-2">
      <!-- Dashboard Banner for authenticated users -->
      <div v-if="authStore.isAuthenticated" class="mt-6 mb-2">
        <router-link 
          to="/dashboard" 
          class="flex items-center justify-between bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl px-6 py-4 border border-blue-100 hover:shadow-md transition-all duration-200 group"
        >
          <div class="flex items-center gap-3">
            <div class="p-2 bg-blue-600 rounded-lg">
              <Squares2X2Icon class="w-5 h-5 text-white" />
            </div>
            <div>
              <p class="font-semibold text-gray-800 group-hover:text-blue-700 transition-colors">{{ t('home.yourDashboard') }}</p>
              <p class="text-sm text-gray-500">{{ t('home.dashboardSubtitle') }}</p>
            </div>
          </div>
          <ArrowRightIcon class="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
        </router-link>
      </div>

      <!-- Destination Section -->
      <div ref="destinationSection" id="destination-section" class="mt-10 w-full">

        <!-- Heading row -->
        <div class="flex items-center justify-between mb-4">
          <div>
            <h2 class="text-xl font-bold text-gray-800">
              {{ destinationSectionHeading }}
            </h2>
            <p v-if="destinationSectionSubheading" class="text-sm text-gray-500 mt-0.5">{{ destinationSectionSubheading }}</p>
          </div>
          <!-- Compact departure city switcher (only when multiple airports detected) -->
          <div v-if="departureCities.length > 1" class="flex items-center gap-1.5 flex-wrap justify-end">
            <button
              v-for="city in departureCities"
              :key="city.id"
              @click="handleDepartureCitySelect(city)"
              :class="[
                'px-3 py-1 rounded-full border text-xs font-semibold transition-all duration-200',
                selectedDepartureCity && selectedDepartureCity.name === city.name && !isShowingTopCities
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300 hover:text-blue-600'
              ]"
            >
              {{ city.display }}
            </button>
            <button
              @click="showPopularDestinations"
              :class="[
                'px-3 py-1 rounded-full border text-xs font-semibold transition-all duration-200',
                isShowingTopCities
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300 hover:text-blue-600'
              ]"
            >
              {{ t('home.trending') }}
            </button>
          </div>
        </div>

        <div class="min-h-[226px]">
          <!-- Loading state -->
          <div v-if="isLoadingDestinations" role="status" :aria-label="t('home.loadingDestinations')">
            <div class="flex gap-4 overflow-hidden pb-2" aria-hidden="true">
              <div
                v-for="i in 3"
                :key="i"
                class="flex-shrink-0 w-44 overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm"
              >
                <div class="h-32 animate-pulse bg-gray-200"></div>
                <div class="space-y-2 px-3 py-2.5">
                  <div class="h-3.5 w-24 animate-pulse rounded bg-gray-200"></div>
                  <div class="h-3 w-16 animate-pulse rounded bg-gray-100"></div>
                </div>
              </div>
            </div>
            <span class="sr-only">{{ t('home.loadingDestinations') }}</span>
          </div>

          <!-- Error state -->
          <div v-else-if="destinationError" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {{ destinationError }}
          </div>

          <!-- Destination strip -->
          <DestinationStrip
            v-else-if="destinations.length > 0"
            :destinations="destinations"
            @select="exploreDestination"
            @explore="navigateToExplore"
          />

          <!-- Explore nudge -->
          <p v-if="destinations.length > 0" class="mt-4 text-sm text-gray-400 text-center">
            {{ t('home.notSureWhereToGo') }}
            <router-link to="/explore" class="text-blue-500 hover:text-blue-700 font-medium transition-colors">{{ t('home.browseAllDestinations') }}</router-link>
          </p>
        </div>
      </div>

      <!-- Flight Ideas inspiration section -->
      <div class="mt-16">
        <div class="mb-5">
          <h2 class="text-2xl font-bold text-gray-800">{{ t('home.flightIdeasHeading') }}</h2>
          <p class="text-gray-500 mt-1 text-sm">{{ t('home.flightIdeasSubheading') }}</p>
        </div>
        <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <router-link
            v-for="idea in flightIdeas"
            :key="idea.path"
            :to="idea.path"
            class="group flex items-center justify-between bg-white rounded-xl border border-gray-100 shadow-sm px-5 py-4 hover:border-blue-300 hover:shadow transition-all"
          >
            <div>
              <p class="font-semibold text-gray-800 group-hover:text-blue-600 transition-colors">{{ idea.title }}</p>
              <p class="text-xs text-gray-400 mt-0.5">{{ idea.subtitle }}</p>
            </div>
            <ArrowRightIcon class="w-4 h-4 text-gray-300 group-hover:text-blue-500 transition-colors flex-shrink-0 ml-3" />
          </router-link>
        </div>
        <div class="mt-4 text-center">
          <router-link to="/flight-ideas" class="text-blue-500 hover:text-blue-700 font-medium text-sm transition-colors">
            {{ t('home.browseAllFlightIdeas') }}
          </router-link>
        </div>
      </div>

      <!-- Why ProperFlight? Section -->
      <div class="mt-16">
        <div class="mb-8">
          <h2 class="text-2xl font-bold text-gray-800">{{ t('home.whyHeading') }}</h2>
          <p class="text-gray-500 mt-1 text-sm">{{ t('home.whySubheading') }}</p>
        </div>
        <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div v-for="feature in whyFeatures" :key="feature.title" class="flex items-start gap-3 bg-white rounded-xl border border-gray-100 shadow-sm px-5 py-4">
            <span class="mt-0.5 flex-shrink-0 flex items-center justify-center h-5 w-5 rounded-full bg-green-100">
              <svg class="h-3 w-3 text-green-600" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>
            </span>
            <div>
              <p class="text-sm font-semibold text-gray-800">{{ feature.title }}</p>
              <p class="text-xs text-gray-500 mt-0.5 leading-relaxed">{{ feature.desc }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- FAQ Section -->
      <div id="faq-section" class="mt-16 mb-8 w-full">
        <h2 class="text-2xl font-bold text-gray-800 mb-1 text-center">{{ t('home.faqHeading') }}</h2>
        <p class="text-gray-400 text-sm text-center mb-6">{{ t('home.faqSubheading') }} <a href="mailto:contact@properflight.com" class="text-blue-500 hover:text-blue-700 transition-colors">{{ t('home.faqContact') }}</a>.</p>
        <div class="space-y-3">
          <div
            v-for="(item, index) in landingFaqs"
            :key="'lf-' + index"
            class="bg-white rounded-xl border transition-all duration-200"
            :class="landingOpenIndex === index ? 'border-blue-200 shadow-md' : 'border-gray-100 shadow-sm hover:border-gray-200 hover:shadow'"
          >
            <button
              class="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
              :aria-expanded="landingOpenIndex === index ? 'true' : 'false'"
              @click="landingOpenIndex = landingOpenIndex === index ? null : index"
            >
              <span
                class="font-semibold text-base transition-colors"
                :class="landingOpenIndex === index ? 'text-blue-600' : 'text-gray-800'"
              >
                {{ item.q }}
              </span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 flex-shrink-0 transition-transform duration-300"
                :class="landingOpenIndex === index ? 'rotate-180 text-blue-500' : 'text-gray-400'"
                fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"
                aria-hidden="true"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div
              class="overflow-hidden transition-all duration-300 ease-in-out"
              :class="landingOpenIndex === index ? 'max-h-64' : 'max-h-0'"
            >
              <p class="px-6 pb-5 text-gray-500 leading-relaxed text-sm">
                {{ item.a }}
                <router-link v-if="item.link" :to="item.link" class="text-blue-600 hover:underline ml-1">{{ item.linkText }}</router-link>
              </p>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<style scoped>
.tab-inactive-pulse {
  animation: tab-text-flash 4s ease-in-out infinite;
}

@keyframes tab-text-flash {
  0%   { color: rgba(255, 255, 255, 0.8); background-color: transparent; }
  15%  { color: rgba(255, 255, 255, 1); background-color: rgba(255, 255, 255, 0.04); }
  30%  { color: rgba(255, 255, 255, 0.8); background-color: transparent; }
  100% { color: rgba(255, 255, 255, 0.8); background-color: transparent; }
}
</style>

<script setup>
import { useRouter, useRoute } from 'vue-router';
import SearchForm from '@/components/SearchForm.vue';
import { ref, computed, onMounted, onBeforeUnmount, onUnmounted, watch, nextTick, defineAsyncComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePermissionStore } from '@/stores/permission'; // Import permission store
import { useAuthStore } from '@/stores/authStore';  // Import auth store
import { Squares2X2Icon, ArrowRightIcon, GlobeEuropeAfricaIcon } from '@heroicons/vue/24/outline';
import axios from '@/axios'; // Import the configured Axios instance
const BuddySearchForm = defineAsyncComponent(() => import('@/components/BuddySearchForm.vue'));
import DestinationStrip from '@/components/DestinationStrip.vue';
import { isConfidentLocation, persistLocation, resolveInitialLocation } from '@/utils/locationResolver';
import { getVisitorId } from '@/utils/visitorId';
import { trackOriginChanged } from '@/utils/originAnalytics';
import { localePath } from '@/router';

const authStore = useAuthStore();  // Add auth store
const route = useRoute();
const { t, tm } = useI18n();

// Landing page FAQ
const landingOpenIndex = ref(null);

const flightIdeas = computed(() => tm('home.flightIdeasLinks'))
const whyFeatures = computed(() => tm('home.whyFeatures'))
const landingFaqs = computed(() => tm('home.faqs'))

// Search type switcher
const activeSearchType = ref('basic');
const buddyFormKey = ref(0);

const searchTypes = computed(() => [
  { id: 'basic',   ...tm('home.searchTypes.basic'),   isNew: false },
  { id: 'buddy',   ...tm('home.searchTypes.buddy'),   isNew: false },
  { id: 'weather', ...tm('home.searchTypes.weather'), isNew: true  },
])

const currentTitle = computed(() => searchTypes.value.find(s => s.id === activeSearchType.value)?.title || 'Flights that fit your schedule');
const currentSubtitle = computed(() => searchTypes.value.find(s => s.id === activeSearchType.value)?.subtitle || '');

const setSearchType = (type) => {
  activeSearchType.value = type;
  const routePaths = { basic: '/searches/basic', buddy: '/searches/buddy', weather: '/searches/weather' }
  isInternalSwitch = true
  router.replace({ path: localePath(routePaths[type]), query: route.query })
};

const updateBuddyDestinations = (destinations) => {
  searchParams.value = { ...searchParams.value, to: destinations };
};


// Lets a destination tile run the search directly via the form's exposed
// submitSearch(), rather than only prefilling it.
const searchFormRef = ref(null);

// Reactive states
const isFixed = ref(false);
const cityName = ref(null);
const cityCode = ref(null);
const countryCode = ref(null);
const approximateCurrency = ref(null);

// Track selected departure city
const selectedDepartureCity = ref(null);

// Methods
const handleScroll = () => {
  // Find the split line element dynamically
  const splitLineElement = document.querySelector('.bg-gradient-to-r.via-gray-300')
  
  if (splitLineElement) {
    // Calculate the exact offset from the top of the page
    const rect = splitLineElement.getBoundingClientRect()
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    
    // Calculate the scroll threshold with a small buffer
    const scrollThreshold = rect.top - 50 // 50px buffer for safety
    
    // Detailed logging for debugging
    console.log({
      splitLineElementTop: rect.top,
      scrollTop,
      scrollThreshold,
      currentScrollY: window.scrollY
    })
    
    // Set fixed state based on scroll position
    isFixed.value = window.scrollY >= scrollThreshold
  }
};


// Lifecycle hooks
onMounted(async () => {
  const routeTypeMap = { Home: 'basic', BasicSearch: 'basic', BuddySearch: 'buddy', WeatherSearch: 'weather' }
  const baseName = route.name ? String(route.name).replace(/Locale$/, '') : ''
  activeSearchType.value = routeTypeMap[baseName] || 'basic'

  console.log('Authentication Status:', authStore.isAuthenticated)
  console.log('Session Token present:', !!localStorage.getItem('pfSessionToken'))

  // Registered before the first resolve: detection can settle (or be revised after a network
  // change) while this handler is still being awaited.
  window.addEventListener('location-resolved', handleLocationResolved);
  applyResolvedLocation(await resolveInitialLocation());

  // Pre-fill destination from query params (e.g. from Explore / Flight Ideas pages)
  // Supports comma-separated multi-city: ?to=WAW,BCN&toName=Warsaw,Barcelona
  // Optional departure override from Flight Ideas: ?from=LON&fromName=London
  // Optional trip type: ?tripType=return|one-way
  const toCode = route.query.to
  const toName = route.query.toName
  const fromCodeParam = route.query.from
  const fromNameParam = route.query.fromName
  if (toCode && toName) {
    const codes = toCode.split(',')
    const names = toName.split(',')
    const toCities = codes.map((code, i) => ({
      id: code, display: names[i] || code, type: 'city', code: code
    }))
    const today = new Date()
    const thirtyDays = new Date(today)
    thirtyDays.setDate(today.getDate() + 30)
    const fromCode = fromCodeParam || cityCode.value || 'LON'
    const fromName = fromNameParam || cityName.value || 'London'
    searchParams.value = {
      from: [{ id: fromCode, display: fromName, type: 'city', code: fromCode }],
      to: toCities,
      selectedDates: { start: today, end: thirtyDays },
      tripType: route.query.tripType === 'return' ? 'return' : 'one-way',
    }
  } else if (fromCodeParam && fromNameParam) {
    // Departure-only deep link (e.g. "flights from London" hub CTA)
    const today = new Date()
    const thirtyDays = new Date(today)
    thirtyDays.setDate(today.getDate() + 30)
    searchParams.value = {
      from: [{ id: fromCodeParam, display: fromNameParam, type: 'city', code: fromCodeParam }],
      to: [],
      selectedDates: { start: today, end: thirtyDays },
      tripType: route.query.tripType === 'return' ? 'return' : 'one-way',
    }
  }

  // ?autorun=1 means the visitor already chose a destination on another page
  // (a Flight Ideas card) and arrived here only to see results. Landing them on
  // a filled-in form they still have to submit is the same friction the
  // homepage tiles used to have, at the funnel's most expensive step.
  if (route.query.autorun === '1' && toCode && toName) {
    await nextTick()
    if (searchFormRef.value?.submitSearch) {
      searchFormRef.value.submitSearch(searchParams.value)
    }
  }

  // Fetch destinations for the Destination Section
  try {
    await fetchDestinations();
  } catch (error) {
    console.error('Error fetching destinations:', error);
  }

  window.addEventListener('scroll', handleScroll);
  handleScroll();
});

// A city we are not confident about is left out entirely: the strip then falls back to trending
// destinations instead of promising "best deals from" a city the visitor is not in.
const applyResolvedLocation = (location) => {
  if (!isConfidentLocation(location)) return;
  cityName.value = location.city.display_name || location.city.name;
  cityCode.value = location.city.code;
  countryCode.value = location.city.country_code;
  approximateCurrency.value = location.currency;
};

const handleLocationResolved = async () => {
  applyResolvedLocation(await resolveInitialLocation());
  try {
    await fetchDestinations();
  } catch (error) {
    console.error('Error re-fetching destinations after location resolved:', error);
  }
};

// Watch route changes to update activeSearchType
// If navigation came from navbar/direct link (not tab switcher), reset forms
let isInternalSwitch = false;
watch(() => route.name, (newRouteName) => {
  // Strip the 'Locale' suffix so locale-prefixed routes (e.g. BasicSearchLocale) map correctly
  const baseName = newRouteName ? newRouteName.replace(/Locale$/, '') : ''
  const routeTypeMap = { Home: 'basic', BasicSearch: 'basic', BuddySearch: 'buddy', WeatherSearch: 'weather' }
  const newType = routeTypeMap[baseName] || 'basic'

  if (!isInternalSwitch) {
    // External navigation (navbar, direct link) → reset to defaults
    searchParams.value = {};
    buddyFormKey.value += 1;
  }
  isInternalSwitch = false;

  activeSearchType.value = newType;
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll);
  window.removeEventListener('location-resolved', handleLocationResolved);
});

// Search params state
const searchParams = ref({});
const isLoading = ref(false);
const searchFormContainer = ref(null);
const destinationSection = ref(null);

// Destination section data and state
const destinations = ref([]);
const isLoadingDestinations = ref(false);
const destinationError = ref('');
const isShowingTopCities = ref(false);

// New variables for departure city selection
const departureCities = ref([]);
const destinationsByCity = ref({});

// Heading/subheading for the destination strip
const destinationSectionHeading = computed(() => {
  if (!isShowingTopCities.value && selectedDepartureCity.value) {
    return t('home.bestDealsFrom', { city: selectedDepartureCity.value.name })
  }
  if (!isShowingTopCities.value && cityName.value) {
    return t('home.bestDealsFrom', { city: cityName.value })
  }
  return t('home.trendingDestinations')
})

const destinationSectionSubheading = computed(() => {
  if (!isShowingTopCities.value && (selectedDepartureCity.value || cityName.value)) {
    return t('home.cheapestRoutes')
  }
  return t('home.mostPopularCities')
})

// Function to update destinations based on selected departure city
const updateDestinationsForSelectedCity = () => {
  if (selectedDepartureCity.value && destinationsByCity.value[selectedDepartureCity.value.name]) {
    // Get destinations for the selected city and add _imgLoaded property
    destinations.value = destinationsByCity.value[selectedDepartureCity.value.name].map(destination => ({
      ...destination,
      _imgLoaded: false
    }));
  } else {
    destinations.value = [];
  }
};

// Function to handle departure city selection
const handleDepartureCitySelect = async (city) => {
  selectedDepartureCity.value = {
    name: city.name,
    code: city.code,
    display: city.display,
    type: 'city'
  };
  console.log('Selected departure city:', selectedDepartureCity.value);

  trackOriginChanged(city.code, 'landing-city-picker');

  // Update localStorage with the new selection
  persistLocation({
    city: {
      code: city.code,
      name: city.name,
      country_code: countryCode.value || '',
    },
  }, 'manual');
  
  // Set isShowingTopCities to false when a specific city is selected
  isShowingTopCities.value = false;
  updateDestinationsForSelectedCity();
  // If the selected city has no priced routes, pad with top destinations
  if (destinations.value.length === 0) {
    await fetchTopCities();
  }
};

// Function to show popular destinations
const showPopularDestinations = async () => {
  isShowingTopCities.value = true;
  await fetchTopCities();
};

// Function to fetch destinations from the API
const fetchDestinations = async () => {
  try {
    isLoadingDestinations.value = true;
    destinationError.value = '';
    isShowingTopCities.value = false;
    
    const departureCityCode = cityCode.value;
    const departureCountryCode = countryCode.value;
    
    if (!departureCityCode) {
      await fetchTopCities();
      return;
    }
    
    // Get nearby cities from the permission store
    const permissionStore = usePermissionStore();
    const nearbyCities = [...(permissionStore.nearbyCities || [])];
    
    // If no nearby cities, use the current city as a nearby city
    if (nearbyCities.length === 0 && departureCityCode && departureCountryCode) {
      nearbyCities.push({
        name: cityName.value || '',
        code: departureCityCode,
        country_code: departureCountryCode,
        location: {
          latitude: localStorage.getItem('latitude') || 0,
          longitude: localStorage.getItem('longitude') || 0
        },
        currency: {
          currency_name: '',
          currency_code: localStorage.getItem('currency') || approximateCurrency.value || 'USD',
          currency_position: 'before'
        }
      });
    }
    
    console.log(`Fetching destinations from ${nearbyCities.length} nearby cities`);
    
    // Encode nearby cities as JSON string and pass it in the request
    const nearbyCitiesParam = encodeURIComponent(JSON.stringify(nearbyCities));
    const userCurrency = localStorage.getItem('preferredCurrency') || localStorage.getItem('currency') || approximateCurrency.value || 'USD';
    const response = await axios.get(`/search/results/suggestions/?nearbyCities=${nearbyCitiesParam}&currency=${userCurrency}`);
    
    // The response now contains departure_cities and results_by_city
    const { departure_cities, results_by_city } = response.data;
    
    // Store the departure cities and results by city
    departureCities.value = departure_cities.map(city => ({
      id: city.code, 
      name: city.name,
      code: city.code,
      display: `${city.name} (${city.code})`
    })) || [];
    console.log('Departure cities:', departureCities.value);
    destinationsByCity.value = results_by_city || {};
    
    // Auto-select the city that matches the user's closest airport (cityCode),
    // falling back to the first city that has results if no exact match found.
    if (departureCities.value.length > 0) {
      const userCityCode = departureCityCode;
      const matchedCity = departureCities.value.find(c => c.code === userCityCode)
        || departureCities.value[0];
      selectedDepartureCity.value = {
        name: matchedCity.name,
        code: matchedCity.code,
        display: matchedCity.display,
        type: 'city'
      };
    }
    
    // Get destinations for the selected departure city
    updateDestinationsForSelectedCity();
    
    // If no destinations are found, fetch top cities as a fallback
    if (destinations.value.length === 0) {
      await fetchTopCities();
    }
    
    console.log('Departure cities:', departureCities.value);

  } catch (error) {
    console.error('Error fetching destinations:', error);
    destinationError.value = error.response?.data?.error || t('errors.failedLoadDestinationsLater');
    // Try to fetch top cities as a fallback
    await fetchTopCities();
  } finally {
    isLoadingDestinations.value = false;
  }
};

// Function to fetch top cities as a fallback when no destinations are found
const fetchTopCities = async () => {
  try {
    isLoadingDestinations.value = true;
    destinationError.value = '';
    
    // Call the top-cities endpoint to get top 15 cities sorted by dst_rank
    const response = await axios.get('/search/top-cities/?limit=15');
    console.log('Top cities response:', response.data);
    
    if (response.data && Array.isArray(response.data)) {
      // Map the response to the destinations format
      destinations.value = response.data.map(city => ({
        ...city,
        // Ensure these properties exist for compatibility with the UI
        city: city.city || city.name,
        city_code: city.city_code,
        country: city.country,
        country_code: city.country_code,
        price: city.price || null,
        _imgLoaded: false
      }));
      
      // Set flag to indicate we're showing top cities instead of destinations from a specific city
      isShowingTopCities.value = true;
      
      console.log('Fetched top cities as fallback:', destinations.value.length);
    } else {
      console.error('Invalid response format from top-cities endpoint');
      destinations.value = [];
    }
  } catch (error) {
    console.error('Error fetching top cities:', error);
    destinations.value = [];
  } finally {
    isLoadingDestinations.value = false;
  }
};

const navigateToExplore = async () => {
  await router.push(localePath('/explore'));
  await nextTick();
  const s = document.querySelector('.overflow-auto');
  if (s) s.scrollTop = 0;
};

// Function to handle clicking on a destination
const exploreDestination = async (destination, index = null) => {
  console.log('Exploring destination:', destination);

  // The destination strip is the main content of `/`, where ~99% of sessions
  // land, but nothing between "strip loaded" and "book_click" was measured.
  // has_price separates real deal tiles from the unpriced popular-destination
  // padding, which is the open question: whether those tiles work at all.
  axios.post('/analytics/track-event/', {
    event_type: 'strip_click',
    destination: destination.city_code || destination.city || '',
    price: destination.price ?? null,
    currency: destination.currency || '',
    extra: {
      has_price: destination.price != null,
      position: index,
      origin_city_code: localStorage.getItem('cityCode') || null,
      country: destination.country || null,
      showing_top_cities: isShowingTopCities.value,
    },
    visitor_id: getVisitorId(),
  }).catch(() => {});

  
  // Get today's date and date 30 days from now
  const today = new Date();
  const thirtyDaysFromNow = new Date(today);
  thirtyDaysFromNow.setDate(today.getDate() + 30);
  
  // Get the user's city from the existing selectedDepartureCity or localStorage
  const cityCode = localStorage.getItem('cityCode') || 'LON';
  const cityName = localStorage.getItem('cityName') || 'London';
  
  // Create proper city object format matching DepartureDestination component structure
  const fromCityObject = selectedDepartureCity.value && typeof selectedDepartureCity.value === 'object'
    ? {
        id: selectedDepartureCity.value.code || cityCode,
        display: selectedDepartureCity.value.display || selectedDepartureCity.value.name || cityName,
        type: 'city',
        code: (selectedDepartureCity.value.code || cityCode).replace(/-/g, '')
      }
    : {
        id: cityCode,
        display: cityName,
        type: 'city',
        code: cityCode.replace(/-/g, '')
      };
  
  // Create destination city object with proper structure
  const destinationCityObject = {
    id: destination.city_code || destination.city,
    display: destination.city,
    type: 'city',
    code: (destination.city_code || destination.city || '').replace(/-/g, '')
  };
  
  // One-way deliberately: the tile advertises a one-way fare (the endpoint
  // filters on one_way = TRUE), so searching for a return would show roughly
  // double the price the visitor just clicked. One-way searches also convert
  // slightly better overall (21.9% vs 18.9% to a book click).
  const params = {
    from: [fromCityObject],
    to: [destinationCityObject],
    selectedDates: {
      start: today,
      end: thirtyDaysFromNow,
    },
    tripType: 'one-way',
  };

  // Keep the parent's copy in sync so the form still reflects the choice if the
  // search cannot be run below.
  searchParams.value = { ...(searchParams.value || {}), ...params };

  // Run the search immediately instead of only prefilling the form.
  //
  // Previously this scrolled back up to the form, waited 500ms and populated
  // the fields, leaving the visitor to find and press "Search" themselves.
  // That is friction at the single most expensive step in the funnel: 3.8% of
  // visitors ever reach a search, but 36.5% of those who do go on to click
  // through to book. Clicking a destination tile is an unambiguous statement of
  // intent, so it should produce results, not a filled-in form.
  await nextTick();
  if (searchFormRef.value?.submitSearch) {
    searchFormRef.value.submitSearch(params);
    return;
  }

  // The form is not mounted (buddy tab is active), so fall back to the old
  // behaviour and let the visitor complete the search by hand.
  scrollToSearchForm();
};

// Error handling
const handleError = (error) => {
  console.error('Search error:', error);
  isLoading.value = false;
};

// Loading state handling
const handleLoading = (loading) => {
  isLoading.value = loading;
};

// Update search params (merge so partial updates like { to: [...] } don't wipe other fields)
const handleSearchParamsUpdate = (params) => {
  searchParams.value = { ...searchParams.value, ...params };
};


const router = useRouter();

// Handle search results - navigate to results page with hash and bag info
const handleSearchResults = (result) => {
  const hash = typeof result === 'string' ? result : result.hash;
  const bagDeepLinks = typeof result === 'object' ? result.bagDeepLinks : null;
  const totalCheckedBags = typeof result === 'object' ? result.totalCheckedBags : 0;
  const totalCabinBags = typeof result === 'object' ? result.totalCabinBags : 0;

  // Guard against an undefined hash so we don't trigger a "Missing required param" router error.
  if (!hash) {
    handleError(t('errors.noResultsFound'));
    return;
  }

  // Store bag info in sessionStorage for the results page to use
  if (bagDeepLinks) {
    sessionStorage.setItem('bagDeepLinks', JSON.stringify(bagDeepLinks));
  }
  sessionStorage.setItem('totalCheckedBags', totalCheckedBags.toString());
  sessionStorage.setItem('totalCabinBags', totalCabinBags.toString());
  sessionStorage.setItem(`resultsSearchParams:${hash}`, JSON.stringify(searchParams.value));

  router.push(localePath('/search/results/' + hash));
};

// Handle navigation between search types
const handleNavigation = (direction) => {
  if (direction === 'weather') {
    setSearchType('weather');
  }
};

// Scroll to search form function – scrolls to the very top of the page so the
// full hero section (including navbar) is visible, not just the form element.
const scrollToSearchForm = () => {
  const scroller = document.querySelector('.overflow-auto') || window;
  scroller.scrollTo({ top: 0, behavior: 'smooth' });
};

onMounted(() => {
  console.log('isSticky.value: ');
});

onUnmounted(() => {
});
</script>

<style scoped>

.animate-gradient {
  background-size: 200% 200%;
  animation: gradient 15s ease infinite;
}

@keyframes gradient {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.loader {
  border: 8px solid #f3f3f3;
  border-top: 8px solid #3498db;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.min-h-screen {
  position: relative;
}

.absolute {
  position: absolute;
}

.hover\:from-amber-250:hover {
  --tw-gradient-from: #fcd34d;
}

.hover\:to-amber-350:hover {
  --tw-gradient-to: #f6b735;
}

.sticky {
  position: sticky;
  top: 0;
}

.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 300ms;
}

.animate-gradient {
  background-size: 300%;
  -webkit-animation: animatedgradient 6s ease infinite alternate;
  -moz-animation: animatedgradient 6s ease infinite alternate;
  animation: animatedgradient 6s ease infinite alternate;
}

@keyframes animatedgradient {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}
</style>
